/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { SubjectContext } from 'blb-contexts/Subject';
import { ToastContextType } from 'blb-contexts/Toast';
import { MeContext } from 'blb-contexts/Me';
import { css, StyleSheet } from 'aphrodite';
import { filter, first, includes, size } from 'lodash';
import { useParams } from 'react-router-dom';
import { LoadingSpinner, LoneIconButton, PopoverList } from '@blend/enterprise-components';
import { createCollatedDocument, deleteDocument, downloadDocument } from 'blb-api/loans';
import { useToasts } from 'blb-components/AuthenticatedApp/Toast';

import { DownloadInkSignInterruptModal } from 'blb-persona/common/DownloadInkSignInterruptModal';
import { DocumentWithTag } from 'blb-ui-types/BridgeTypes';

import { isDocDownloadable } from '../docUtils';
import { documentActions } from './constants';
import { DeleteDocumentModal } from './DeleteDocumentModal/DeleteDocumentModal';
import { DownloadableDocsModal } from './DownloadableDocsModal';
import { ViewDocsModal } from './ViewDocsModal';
import { EcloseDocumentTypeLabels } from 'blb-components/AuthenticatedApp/DashboardPage/DocumentsCard/constants';

const styles = StyleSheet.create({
  actionsWrapper: {
    display: 'flex',
  },
  loadingSpinner: {
    padding: '6px',
  },
});

export interface DocumentDownloadParams {
  Toaster: ToastContextType;
  collateFileName?: string;
  documentTypeLabel?: string;
  documents: DocumentWithTag[];
  loanId: string;
}

export const downloadSingleDocument = (docParams: DocumentDownloadParams) => {
  const { documents, loanId, Toaster, documentTypeLabel } = docParams;

  const { _id, type } = first(documents)!;
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  downloadDocument({ documentId: _id, documentTypeLabel: documentTypeLabel ?? type, loanId, Toaster });
};

export const downloadMultipleDocuments = async (docParams: DocumentDownloadParams) => {
  const { collateFileName, documents, loanId, Toaster, documentTypeLabel } = docParams;

  const files = filter(documents, doc => isDocDownloadable(doc)).map(doc => doc.primaryFile!.url);

  const { data: documentId } = await createCollatedDocument({ collateFileName, files, loanId });
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  downloadDocument({ documentId, documentTypeLabel: documentTypeLabel ?? first(documents)!.type, loanId, Toaster });
};

export const openDocumentViewer = (loanId: string) => {
  window.open(`${loanId}/sign-ahead-documents`, '_blank', 'noopener,noreferrer');
};

export namespace DocumentActions {
  export interface Props {
    areDocDetailsOpen: boolean;
    collateFileName: string;
    documentTypeLabel?: string;
    documents: DocumentWithTag[];
    isUploaded: boolean;
    removeDoc?: (id: string) => void;
    setDocDetailsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setShouldShowUnsignedModal: React.Dispatch<React.SetStateAction<boolean>>;
    shouldShowUnsignedModal: boolean;
    title: string;
    areDocDetailsViewOnlyOpen: boolean;
    setDocDetailsViewOnlyOpen: React.Dispatch<React.SetStateAction<boolean>>;
  }
}

export const DocumentActions: React.FC<DocumentActions.Props> = ({
  areDocDetailsOpen,
  collateFileName,
  documents,
  isUploaded,
  removeDoc,
  setDocDetailsOpen,
  setShouldShowUnsignedModal,
  shouldShowUnsignedModal,
  title,
  documentTypeLabel,
  areDocDetailsViewOnlyOpen,
  setDocDetailsViewOnlyOpen,
}) => {
  const { loanId } = useParams<{ loanId: string }>();
  const context = React.useContext(SubjectContext);
  const [isMultiDownloadInProgress, setMultiDownloadInProgress] = React.useState<boolean>(false);
  const [isDeleteOpen, setDeleteOpen] = React.useState<boolean>(false);
  const [isUnsignedModalOpen, setUnsignedModalOpen] = React.useState<boolean>(false);
  const Toaster = useToasts();
  const me = React.useContext(MeContext);

  const multipleDocs = size(documents) > 1;
  const viewDocsAction = {
    id: 'viewDetails',
    label: 'View details',
    onClick: () => setDocDetailsOpen(true),
  };
  const viewDocsActionHybridESign = {
    id: 'viewDocuments',
    label: 'View details',
    onClick: () => setDocDetailsViewOnlyOpen(true),
  };

  const deleteDoc = async () => {
    await deleteDocument(loanId, first(documents)!._id);
    setDeleteOpen(false);
    removeDoc && removeDoc(first(documents)!._id);
  };

  const downloadDocuments = async () => {
    if (multipleDocs) {
      setMultiDownloadInProgress(true);
      await downloadMultipleDocuments({
        collateFileName,
        documents,
        documentTypeLabel,
        loanId,
        Toaster,
      });
      setMultiDownloadInProgress(false);
    } else {
      downloadSingleDocument({
        documents,
        documentTypeLabel,
        loanId,
        Toaster,
      });
    }
  };

  const openUnsignedESignModal = () => {
    // Download Interrupt Modal
    // Unsigned E-Sign Notifications
    setUnsignedModalOpen(true);
  };

  const handleUnsignedModalClose = (hasAcknowledged: boolean) => {
    setUnsignedModalOpen(false);

    if (hasAcknowledged) {
      setShouldShowUnsignedModal(false);
    }
  };

  const downloadHandler = shouldShowUnsignedModal ? openUnsignedESignModal : downloadDocuments;

  const shouldShowViewDocuments = () => {
    return (
      (context?.closing?.closingType === 'HYBRID' || context?.closing?.closingType === 'RON') &&
      me?.principalType === 'settlement_agent' &&
      documentTypeLabel === EcloseDocumentTypeLabels.ESIGN_CLOSING_PACKAGE &&
      documents.length > 0 &&
      first(documents)!.actions?.includes('VIEW') &&
      !isDocDownloadable(first(documents)!)
    );
  };

  const showDeleteIcon = () => includes(first(documents)!.actions, documentActions.DELETE);

  const showDownloadIcon = () => isDocDownloadable(first(documents)!);

  const showViewDetails = () => first(documents)!.actions?.includes('VIEW');

  const getViewDocsActionIcons = () => (
    <>
      <LoneIconButton
        icon="visibility"
        iconSize={24}
        noBorder
        noHover
        onClick={() => openDocumentViewer(loanId)}
        theme="tertiary"
      />
      <PopoverList buttonConfigs={[viewDocsActionHybridESign]}>
        <LoneIconButton icon="more_horiz" iconSize={24} noBorder noHover theme="tertiary" />
      </PopoverList>
    </>
  );

  const getDownloadActionIcons = () => (
    <>
      {isMultiDownloadInProgress ? (
        <div className={css(styles.loadingSpinner)}>
          <LoadingSpinner size="lg" theme="grey" />
        </div>
      ) : (
        <>
          {showDownloadIcon() && (
            <LoneIconButton icon="save_alt" iconSize={24} noBorder noHover onClick={downloadHandler} theme="tertiary" />
          )}
          {showViewDetails() && (
            <PopoverList buttonConfigs={[viewDocsAction]}>
              <LoneIconButton icon="more_horiz" iconSize={24} noBorder noHover theme="tertiary" />
            </PopoverList>
          )}
        </>
      )}
      {showDeleteIcon() && (
        <>
          <LoneIconButton icon="delete" noBorder noHover onClick={() => setDeleteOpen(true)} theme="tertiary" />
          <DeleteDocumentModal
            deleteDoc={deleteDoc}
            handleClose={() => setDeleteOpen(false)}
            isOpen={isDeleteOpen}
            title={`Delete "${first(documents)!.fileName}"?`}
          />
        </>
      )}
    </>
  );

  const documentDownloadParams: DocumentDownloadParams = {
    collateFileName,
    documents,
    documentTypeLabel,
    loanId,
    Toaster,
  };

  return (
    <div className={css(styles.actionsWrapper)}>
      {shouldShowUnsignedModal && (
        <DownloadInkSignInterruptModal
          documentDownloadParams={documentDownloadParams}
          handleClose={handleUnsignedModalClose}
          isOpen={isUnsignedModalOpen}
        />
      )}
      {shouldShowViewDocuments() ? getViewDocsActionIcons() : getDownloadActionIcons()}
      <DownloadableDocsModal
        collateFileName={collateFileName}
        docsWithTag={documents}
        documentTypeLabel={documentTypeLabel}
        handleClose={() => setDocDetailsOpen(false)}
        isOpen={areDocDetailsOpen}
        isUploaded={isUploaded}
        title={title}
      />
      <ViewDocsModal
        docsWithTag={documents}
        handleClose={() => setDocDetailsViewOnlyOpen(false)}
        isOpen={areDocDetailsViewOnlyOpen}
        isUploaded={isUploaded}
        title={title}
      />
    </div>
  );
};
